/**
 * 前端日誌記錄工具
 * 用於追蹤用戶行為、錯誤和效能問題
 */

class ClientLogger {
  constructor() {
    this.endpoint = '/api/client-logs';
    this.queue = [];
    this.flushInterval = null;
    this.sessionId = this.generateSessionId();
    this.maxQueueSize = 100;
    this.flushIntervalMs = 10000; // 10秒

    this.startAutoFlush();
    this.setupGlobalErrorHandler();
  }

  /**
   * 產生唯一的 session ID
   */
  generateSessionId() {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * 偵測是否為 In-App Browser
   */
  detectInAppBrowser() {
    const ua = navigator.userAgent || navigator.vendor || window.opera || '';
    
    // 偵測各種 in-app browser
    const inAppBrowserPatterns = [
      { pattern: /FBAN|FBAV/i, name: 'Facebook' },
      { pattern: /Instagram/i, name: 'Instagram' },
      { pattern: /Line/i, name: 'LINE' },
      { pattern: /Lark/i, name: 'Lark' }, // 飛書 (Lark/Feishu)
      { pattern: /MicroMessenger/i, name: 'WeChat' },
      { pattern: /Twitter/i, name: 'Twitter' },
      { pattern: /Pinterest/i, name: 'Pinterest' },
      { pattern: /LinkedInApp/i, name: 'LinkedIn' },
      { pattern: /Snapchat/i, name: 'Snapchat' },
      { pattern: /Telegram/i, name: 'Telegram' },
      { pattern: /WhatsApp/i, name: 'WhatsApp' },
      { pattern: /QQ\//i, name: 'QQ' },
      { pattern: /DingTalk/i, name: 'DingTalk' },
    ];

    for (const browser of inAppBrowserPatterns) {
      if (browser.pattern.test(ua)) {
        return {
          isInAppBrowser: true,
          detectedApp: browser.name,
          userAgent: ua,
        };
      }
    }

    // 額外檢查：是否在 WebView 中（iOS/Android）
    const isWebView = /(iPhone|iPod|iPad).*AppleWebKit(?!.*Safari)|; wv\)/.test(ua);
    
    return {
      isInAppBrowser: isWebView,
      detectedApp: isWebView ? 'Unknown WebView' : '',
      userAgent: ua,
    };
  }

  /**
   * 取得瀏覽器資訊
   */
  getBrowserInfo() {
    const ua = navigator.userAgent;
    let browserName = 'Unknown';
    let browserVersion = 'Unknown';

    // 瀏覽器偵測（按優先順序）
    const browsers = [
      { pattern: /Edge\/(\S+)/, name: 'Edge' },
      { pattern: /Edg\/(\S+)/, name: 'Edge' }, // Chromium-based Edge
      { pattern: /OPR\/(\S+)/, name: 'Opera' },
      { pattern: /Chrome\/(\S+)/, name: 'Chrome' },
      { pattern: /Safari\/(\S+)/, name: 'Safari' },
      { pattern: /Firefox\/(\S+)/, name: 'Firefox' },
      { pattern: /(MSIE|Trident).*rv:(\S+)/, name: 'IE' },
    ];

    for (const browser of browsers) {
      const match = ua.match(browser.pattern);
      if (match) {
        browserName = browser.name;
        browserVersion = match[1] || match[2] || 'Unknown';
        break;
      }
    }

    // Safari 需要特別處理（因為 Chrome 也包含 Safari）
    if (browserName === 'Safari' && /Chrome/.test(ua)) {
      browserName = 'Chrome';
      const chromeMatch = ua.match(/Chrome\/(\S+)/);
      if (chromeMatch) browserVersion = chromeMatch[1];
    }

    // 偵測是否為移動設備
    const isMobile = /Mobile|Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua);

    return {
      name: browserName,
      version: browserVersion,
      platform: navigator.platform,
      isMobile,
    };
  }

  /**
   * 記錄日誌事件
   */
  log(level, event, data = {}) {
    const inAppBrowserInfo = this.detectInAppBrowser();
    
    const logEvent = {
      timestamp: new Date().toISOString(),
      level,
      event,
      data: {
        ...data,
        // 加入額外的上下文資訊
        screenWidth: window.screen.width,
        screenHeight: window.screen.height,
        windowWidth: window.innerWidth,
        windowHeight: window.innerHeight,
        language: navigator.language,
      },
      userAgent: navigator.userAgent,
      url: window.location.href,
      isInAppBrowser: inAppBrowserInfo.isInAppBrowser,
      detectedApp: inAppBrowserInfo.detectedApp,
      browserInfo: this.getBrowserInfo(),
      sessionId: this.sessionId,
    };

    this.queue.push(logEvent);
    
    // 在開發環境中輸出到 console
    const consoleMethod = level === 'error' ? 'error' : level === 'warn' ? 'warn' : 'log';
    console[consoleMethod](
      `[ClientLogger] ${level.toUpperCase()}: ${event}`,
      data || ''
    );

    // 錯誤立即發送
    if (level === 'error') {
      this.flush();
    }

    // 防止佇列過大
    if (this.queue.length >= this.maxQueueSize) {
      this.flush();
    }
  }

  /**
   * 記錄 info 級別日誌
   */
  info(event, data) {
    this.log('info', event, data);
  }

  /**
   * 記錄 warn 級別日誌
   */
  warn(event, data) {
    this.log('warn', event, data);
  }

  /**
   * 記錄 error 級別日誌
   */
  error(event, data) {
    this.log('error', event, data);
  }

  /**
   * 發送日誌到後端
   */
  async flush() {
    if (this.queue.length === 0) return;

    const logsToSend = [...this.queue];
    this.queue = [];

    try {
      const response = await fetch(this.endpoint, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ logs: logsToSend }),
      });

      if (!response.ok) {
        console.error('[ClientLogger] Failed to send logs:', response.status, response.statusText);
        // 發送失敗，放回佇列（但限制數量避免無限增長）
        this.queue = [...logsToSend.slice(-20), ...this.queue].slice(0, this.maxQueueSize);
      }
    } catch (error) {
      console.error('[ClientLogger] Failed to send logs:', error);
      // 網路錯誤，放回部分日誌
      this.queue = [...logsToSend.slice(-20), ...this.queue].slice(0, this.maxQueueSize);
    }
  }

  /**
   * 啟動自動發送
   */
  startAutoFlush() {
    // 定期自動發送
    this.flushInterval = setInterval(() => {
      this.flush();
    }, this.flushIntervalMs);

    // 頁面卸載前發送（使用 sendBeacon API 確保發送）
    window.addEventListener('beforeunload', () => {
      if (this.queue.length > 0) {
        try {
          // 嘗試使用 sendBeacon（更可靠）
          const blob = new Blob(
            [JSON.stringify({ logs: this.queue })],
            { type: 'application/json' }
          );
          navigator.sendBeacon(this.endpoint, blob);
        } catch (e) {
          // Fallback 到同步 fetch
          this.flush();
        }
      }
    });

    // 頁面隱藏時也發送
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        this.flush();
      }
    });
  }

  /**
   * 設定全域錯誤處理器
   */
  setupGlobalErrorHandler() {
    // 捕捉未處理的 Promise rejection
    window.addEventListener('unhandledrejection', (event) => {
      this.error('unhandled_promise_rejection', {
        reason: event.reason?.toString() || 'Unknown',
        promise: event.promise?.toString() || 'Unknown',
      });
    });

    // 捕捉全域錯誤
    window.addEventListener('error', (event) => {
      this.error('global_error', {
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        error: event.error?.toString() || 'Unknown',
      });
    });
  }

  /**
   * 手動清理（停止自動發送）
   */
  destroy() {
    if (this.flushInterval) {
      clearInterval(this.flushInterval);
      this.flushInterval = null;
    }
    this.flush(); // 最後發送一次
  }
}

// 匯出單例
export const clientLogger = new ClientLogger();
